/** @format */

import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
  AbstractControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { IonicModule, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { isNil, map as lmap, merge, orderBy, sortBy } from 'lodash-es';
import { ModelMapper } from 'model-mapper';
import { NgPipesModule } from 'ngx-pipes';
import { forkJoin, lastValueFrom, map, Observable, of } from 'rxjs';
import {
  Contractor,
  ContractorService,
  EquipmentKind,
  EquipmentKindFireSafetyCategoryName,
  EquipmentTagType,
  EquipmentTagTypeName,
  fade,
  fadeIn,
  fadeOut,
  Family,
  LoaderModule,
} from 'sesio-lib';
import { SubSink } from 'subsink';
import { EquipmentRequest } from '../../../_classes/equipment-request/equipment-request.class';
import {
  CreateEquipmentRequestDto,
  UpdateEquipmentRequestDto,
} from '../../../_classes/equipment-request/equipment-request.dto';
import { EquipmentKindService } from '../../../_services/equipment-kind.service';
import EquipmentRequestService from '../../../_services/equipment-request.service';
import { EquipmentService } from '../../../_services/equipment.service';
import { UserService } from '../../../_services/user.service';
import { ConfirmDialogModule } from '../../confirm-dialog/confirm-dialog.module';
import { OrganizationalUnitModule } from '../../organizational-unit/organizational-unit.module';
import { RealEstateStructureModule } from '../../real-estate-structure/real-estate-structure.module';

interface IDisplayEquipmentKind extends EquipmentKind {
  categoryLabel: string;
}

type DataType = {
  id: string;
};

@Component({
  templateUrl: './equipment-request-datagrid-edit.modal.html',
  styleUrls: ['./equipment-request-datagrid-edit.modal.scss'],
  animations: [fadeOut, fade, fadeIn],
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    LoaderModule,
    ConfirmDialogModule,
    FlexLayoutModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    FormsModule,
    MatSelectModule,
    NgPipesModule,
    OrganizationalUnitModule,
    RealEstateStructureModule,
    MatInputModule,
  ],
})
export class EquipmentRequestDatagridEditModal implements OnInit, OnDestroy {
  EquipmentTagType = EquipmentTagType;
  EquipmentTagTypeName = EquipmentTagTypeName;

  loading = true;
  updating = false;

  @Input()
  id?: string;

  equipmentRequest?: EquipmentRequest;
  isAdministrator = false;
  formGroup: FormGroup;

  kinds: IDisplayEquipmentKind[];
  contractors: Contractor[];

  public compareWithEntity = (o1: any, o2: any) => o1?.id === o2?.id;

  private subsink = new SubSink();

  constructor(
    private modalCtrl: ModalController,
    private formBuilder: UntypedFormBuilder,
    private userService: UserService,
    private equipmentRequestService: EquipmentRequestService,
    private equipmentService: EquipmentService,
    private equipmentKindService: EquipmentKindService,
    private contractorService: ContractorService,
    private changeDetectorRef: ChangeDetectorRef,
    private translate: TranslateService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.isAdministrator = !this.userService.$user.value?.contractor;

    await this.load();

    this.buildFormGroup();

    this.loading = false;
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }

  private async load() {
    if (this.id) {
      this.equipmentRequest = await this.equipmentRequestService.get(this.id).toPromise();
    }

    this.kinds = sortBy(
      lmap(await this.equipmentKindService.list({ families: [Family.FIRE_SAFETY] }).toPromise(), kind =>
        merge(kind, {
          categoryLabel: EquipmentKindFireSafetyCategoryName[kind.category] || this.translate.instant(kind.category),
        }),
      ),
      ['categoryLabel', 'name'],
      ['asc', 'asc'],
    );

    this.contractors = orderBy(
      await lastValueFrom(this.contractorService.list({ families: [Family.FIRE_SAFETY], fields: ['name'] })),
      ['name'],
    );
  }

  private buildFormGroup() {
    const contractor = this.equipmentRequest?.contractor ?? this.userService.$user.value.contractor;

    this.formGroup = this.formBuilder.group({
      kind: this.formBuilder.control(
        {
          value: this.equipmentRequest?.kind,
          disabled: !!this.equipmentRequest,
        },
        [Validators.required, this.validateEntity],
      ),
      organizationalUnit: this.formBuilder.control(
        {
          value: this.equipmentRequest?.organizationalUnit,
          disabled: !!this.equipmentRequest,
        },
        [Validators.required, this.validateEntity],
      ),
      realEstateStructure: this.formBuilder.control(
        {
          value: this.equipmentRequest?.realEstateStructure,
          disabled: !!this.equipmentRequest,
        },
        [Validators.required, this.validateEntity],
      ),
      contractor: this.formBuilder.control(
        {
          value: contractor,
          disabled: !!this.equipmentRequest || !this.isAdministrator,
        },
        [Validators.required, this.validateEntity],
      ),
      state: this.formBuilder.control(
        {
          value: this.equipmentRequest?.state ?? 4,
          disabled: false,
        },
        [Validators.required],
      ),
      externalReference: this.formBuilder.control(
        {
          value: this.equipmentRequest?.externalReference,
          disabled: !contractor,
        },
        [Validators.required],
        [this.validateExternalReference],
      ),
      tagType: this.formBuilder.control({
        value: this.equipmentRequest?.tagType,
        disabled: false,
      }),
      tagCode: this.formBuilder.control({
        value: this.equipmentRequest?.tagCode,
        disabled: false,
      }),

      location: this.formBuilder.control({
        value: this.equipmentRequest?.location,
        disabled: false,
      }),

      comment: this.formBuilder.control({
        value: this.equipmentRequest?.comment,
        disabled: false,
      }),
    });

    this.subsink.add(
      this.formGroup.get('contractor')!.valueChanges.subscribe(value => {
        if (value) this.formGroup.get('externalReference')!.enable();
        else this.formGroup.get('externalReference')!.disable();
      }),
    );
  }

  public async dismiss(reload?: boolean): Promise<void> {
    await this.modalCtrl.dismiss(reload, 'dismiss');
  }

  public async addNew() {
    this.updating = true;
    try {
      await lastValueFrom(
        this.equipmentRequestService.create(new ModelMapper(CreateEquipmentRequestDto).map(this.formGroup.value)),
      );
      this.dismiss(true);
    } finally {
      this.updating = false;
    }
  }

  public async edit(): Promise<void> {
    this.updating = true;
    try {
      await lastValueFrom(
        this.equipmentRequestService.update(
          this.equipmentRequest.id,
          new ModelMapper(UpdateEquipmentRequestDto).map(
            merge(this.formGroup.value, { contractor: this.equipmentRequest.contractor }),
          ),
        ),
      );
      this.dismiss(true);
    } finally {
      this.updating = false;
    }
  }

  public async remove() {
    this.updating = true;
    try {
      await lastValueFrom(this.equipmentRequestService.remove(this.equipmentRequest.id));
      this.dismiss(true);
    } finally {
      this.updating = false;
    }
  }

  public async reject(reason: string) {
    this.updating = true;
    try {
      await lastValueFrom(this.equipmentRequestService.reject(this.equipmentRequest.id, reason));
      this.dismiss(true);
    } finally {
      this.updating = false;
    }
  }

  public async validate() {
    this.updating = true;
    try {
      await lastValueFrom(this.equipmentRequestService.validate(this.equipmentRequest.id));
      this.dismiss(true);
    } finally {
      this.updating = false;
    }
  }

  private validateEntity = (control: AbstractControl): { [key: string]: any } | null => {
    const forbidden = control.value && isNil(control.value.id);
    return forbidden ? { invalidGroup: { value: control.value } } : null;
  };

  private validateExternalReference = (control: AbstractControl): Observable<ValidationErrors | null> => {
    if (!control.dirty) return of(null);

    const contractorId = control.parent.get('contractor')?.value?.id;
    if (!contractorId) return of(null);

    return forkJoin([
      this.equipmentService
        .externalReferenceExists(contractorId, control.value)
        .pipe(map(res => (res?.length > 0 ? { referenceExists: true } : null))),
      this.equipmentRequestService
        .externalReferenceExists(contractorId, control.value)
        .pipe(map(res => (res?.length > 0 ? { referenceExists: true } : null))),
    ]).pipe(map(([firstValue, secondValue]) => firstValue ?? secondValue ?? null));
  };
}
